import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/components/Layout/PageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Common/Card';
import { Badge } from '@/components/Common/badge';
import { Button } from '@/components/Common/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Common/tabs';
import Modal from '@/components/Common/Modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Common/select';
import {
  useUnprocessedEvaluations,
  useProcessedEvaluations,
  useProcessEvaluation,
  useSatisfactionMetrics,
  useChatbotMetrics,
  EvaluationResponse,
  ChatbotMetrics
} from '@/services/evaluations';
import { ThumbsDown, CheckCircle, TrendingUp, ChartBarIcon } from 'lucide-react';
import notify from '@/lib/notify';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

export const EvaluationsDashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? fr : enUS;
  const [selectedEval, setSelectedEval] = useState<EvaluationResponse | null>(null);
  const [adminNote, setAdminNote] = useState('');
  // Default to "since always" so first-time users see all available data
  // instead of an empty 30-day window with no guidance.
  const [dayFilter, setDayFilter] = useState(36500);
  const [activeTab, setActiveTab] = useState('unprocessed');

  const { data: unprocessed, refetch: refetchUnprocessed } = useUnprocessedEvaluations();
  const { data: processed, refetch: refetchProcessed } = useProcessedEvaluations();
  const { data: satisfaction } = useSatisfactionMetrics(dayFilter);
  const { data: chatbotMetrics } = useChatbotMetrics(dayFilter);
  const processEvaluation = useProcessEvaluation();

  // Charger la note admin existante quand on ouvre un feedback traité
  useEffect(() => {
    if (selectedEval) {
      setAdminNote(selectedEval.admin_note || '');
    }
  }, [selectedEval]);

  const handleProcess = async () => {
    if (!selectedEval) return;

    try {
      await processEvaluation.mutateAsync({
        evaluationId: selectedEval.id,
        adminNote
      });

      notify.success(t('evaluations.processSuccess'));
      setSelectedEval(null);
      setAdminNote('');
      refetchUnprocessed();
      refetchProcessed();
    } catch {
      notify.error(t('evaluations.processError'));
    }
  };

  return (
    <PageLayout
      icon={<ChartBarIcon className="h-6 w-6" />}
      title={t('evaluations.title')}
      subtitle={t('evaluations.subtitle')}
      headerRight={
        <Select value={dayFilter.toString()} onValueChange={(v: string) => setDayFilter(parseInt(v))}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{t('evaluations.period.last7')}</SelectItem>
            <SelectItem value="30">{t('evaluations.period.last30')}</SelectItem>
            <SelectItem value="90">{t('evaluations.period.last90')}</SelectItem>
            <SelectItem value="36500">{t('evaluations.period.allTime')}</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <div className="space-y-6">
        {/* KPIs en ligne */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('evaluations.kpis.total')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{satisfaction?.total || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('evaluations.kpis.satisfactionRate')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600 flex items-center gap-2">
                {satisfaction?.positive_rate || 0}%
                <TrendingUp className="h-5 w-5" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('evaluations.kpis.positive')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{satisfaction?.positive || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('evaluations.kpis.negative')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">
                {satisfaction?.negative || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Métriques par Chatbot */}
        <Card>
        <CardHeader>
          <CardTitle>{t('evaluations.byAgent.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.agent')}</th>
                  <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.total')}</th>
                  <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.positive')}</th>
                  <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.negative')}</th>
                  <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.satisfactionRate')}</th>
                </tr>
              </thead>
              <tbody>
                {chatbotMetrics?.chatbots?.map((chatbot: ChatbotMetrics) => (
                  <tr key={chatbot.chatbot_id} className="border-b border-border hover:bg-muted/50">
                    <td className="p-3 font-medium">{chatbot.chatbot_name}</td>
                    <td className="p-3">{chatbot.total}</td>
                    <td className="p-3 text-green-600">{chatbot.positive}</td>
                    <td className="p-3 text-red-600">{chatbot.negative}</td>
                    <td className="p-3">
                      <Badge variant={chatbot.positive_rate >= 70 ? 'default' : 'destructive'}>
                        {chatbot.positive_rate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Feedbacks Non Traités */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ThumbsDown className="h-5 w-5 text-red-500" />
            {t('evaluations.kpis.negative')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="unprocessed">
            <TabsList className="mb-4">
              <TabsTrigger value="unprocessed">
                {t('evaluations.tabs.unprocessed', { count: unprocessed?.length || 0 })}
              </TabsTrigger>
              <TabsTrigger value="processed">
                {t('evaluations.tabs.processed', { count: processed?.length || 0 })}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="unprocessed">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.date')}</th>
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.question')}</th>
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.comment')}</th>
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unprocessed?.map((evaluation: EvaluationResponse) => (
                      <tr key={evaluation.id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-3 text-sm">
                          {format(new Date(evaluation.created_at), 'dd MMM yyyy HH:mm', { locale: dateLocale })}
                        </td>
                        <td className="p-3 text-sm max-w-xs truncate">
                          {evaluation.question}
                        </td>
                        <td className="p-3 text-sm max-w-xs truncate">
                          {evaluation.comment}
                        </td>
                        <td className="p-3">
                          <Button
                            size="sm"
                            onClick={() => setSelectedEval(evaluation)}
                          >
                            {t('evaluations.process')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="processed">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.date')}</th>
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.question')}</th>
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.comment')}</th>
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.adminNote')}</th>
                      <th className="text-left p-3 text-sm font-semibold">{t('evaluations.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processed?.map((evaluation: EvaluationResponse) => (
                      <tr key={evaluation.id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-3 text-sm">
                          {format(new Date(evaluation.created_at), 'dd MMM yyyy HH:mm', { locale: dateLocale })}
                        </td>
                        <td className="p-3 text-sm max-w-xs truncate">
                          {evaluation.question}
                        </td>
                        <td className="p-3 text-sm max-w-xs truncate">
                          {evaluation.comment}
                        </td>
                        <td className="p-3 text-sm max-w-xs truncate">
                          {evaluation.admin_note || '-'}
                        </td>
                        <td className="p-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedEval(evaluation)}
                          >
                            {t('evaluations.view')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modal Détail Feedback */}
      <Modal
        open={!!selectedEval}
        onClose={() => setSelectedEval(null)}
        title={t('evaluations.detail.title')}
        className="max-w-3xl"
        bodyClassName="max-h-[60vh] overflow-y-auto"
        hideDefaultFooter
      >
        {selectedEval && (
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">{t('evaluations.detail.question')}</h4>
              <p className="text-sm bg-muted p-3 rounded">{selectedEval.question}</p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">{t('evaluations.detail.response')}</h4>
              <p className="text-sm bg-muted p-3 rounded whitespace-pre-wrap">
                {selectedEval.response}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">{t('evaluations.detail.userComment')}</h4>
              <p className="text-sm bg-red-950/50 p-3 rounded border border-red-800">
                {selectedEval.comment}
              </p>
            </div>

            {selectedEval.citations && selectedEval.citations.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">
                  {t('evaluations.detail.sources', { count: selectedEval.citations.length })}
                </h4>
                <div className="space-y-2">
                  {selectedEval.citations.map((citation, idx: number) => (
                    <div key={idx} className="text-sm bg-blue-950/50 p-2 rounded border border-blue-800">
                      <div className="font-medium">{citation.document_name || t('aiChatbot.documentDefaultTitle')}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {citation.text?.substring(0, 150)}...
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="font-semibold mb-2">
                {selectedEval.processed ? t('evaluations.detail.adminNote') : t('evaluations.detail.adminNoteOptional')}
              </h4>
              <textarea
                value={adminNote}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAdminNote(e.target.value)}
                placeholder={selectedEval.processed ? t('evaluations.detail.noNote') : t('evaluations.detail.notePlaceholder')}
                rows={3}
                readOnly={selectedEval.processed}
                className={`w-full px-3 py-2 border border-input rounded-md text-sm resize-none ${
                  selectedEval.processed
                    ? 'bg-muted cursor-not-allowed'
                    : 'bg-background focus:outline-none focus:ring-2 focus:ring-ring'
                }`}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setSelectedEval(null)}>
                {t('knowledgeGraph.close')}
              </Button>
              {!selectedEval.processed && (
                <Button onClick={handleProcess} disabled={processEvaluation.isPending}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t('evaluations.detail.markProcessed')}
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
      </div>
    </PageLayout>
  );
};
